<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <h1 class="page-title">空気環境測定基準値情報一覧</h1>
      <div class="mb-2">
        <router-link v-if="isAdmin" :to="{ path: '/create-measure-std', query: getSearchQuery() }" class="btn btn-sm btn-primary me-2">
          空気環境測定基準値情報作成
        </router-link>

        <button type="button" class="btn btn-sm btn-primary me-2" @click="downloadCsv">
          CSVエクスポート
        </button>

        <button v-if="isAdmin" type="button" class="btn btn-sm btn-primary me-2" data-bs-toggle="modal" data-bs-target="#measureStdCsvImportModal">
          CSVインポート
        </button>

        <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value">
        <form class="search-container" @submit.prevent="searchMeasureStds">
          <div class="row g-3 align-items-center">
            <div class="col-auto">
              <label for="searchMeasureTypeId" class="col-form-label">測定種別</label>
            </div>
            <div class="col-auto">
              <select id="searchMeasureTypeId" v-model="searchMeasureTypeId" class="form-select">
                <option value="">すべて</option>
                <option v-for="measureType in measureTypes" :key="measureType.id" :value="measureType.id">{{ measureType.measure_type_nm }}</option>
              </select>
            </div>

            <div class="col-auto">
              <label for="searchMinMaxType" class="col-form-label">最小/最大</label>
            </div>
            <div class="col-auto">
              <select id="searchMinMaxType" v-model="searchMinMaxType" class="form-select">
                <option value="">すべて</option>
                <option value="0">最小</option>
                <option value="1">最大</option>
              </select>
            </div>

            <div class="col-auto">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetMeasureStds">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div v-if="isAdmin" class="modal fade" id="measureStdCsvImportModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5">CSVインポート</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
          </div>
          <div class="modal-body">
            <div v-if="errorMessages">
              <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{ errorMessage.messages[0] }}</p>
              </div>
            </div>
            <div class="my-3">
              <p>空気環境測定基準値CSVファイル選択</p>
              <input type="file" class="form-control" @change="handleFileChange">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
            <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">ID</th>
            <th class="text-nowrap">測定種別</th>
            <th class="text-nowrap">最小/最大</th>
            <th class="text-nowrap">基準値</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="measure_std in measure_stds" :key="measure_std.id">
            <td>{{ measure_std.id }}</td>
            <td>{{ measure_std.m_measure_type?.measure_type_nm }}</td>
            <td>{{ measure_std.min_max_type === 0 ? '最小' : '最大' }}</td>
            <td>{{ measure_std.std_val }}</td>
            <td class="btn-cell text-nowrap">
              <router-link :to="{ name: 'MeasureStdDetail', params: { id: measure_std.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-info">詳細</button>
              </router-link>

              <router-link v-if="isAdmin" :to="{ name: 'MeasureStdEdit', params: { id: measure_std.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-success mx-2">編集</button>
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  getMeasureStds,
  getMeasureTypes,
  exportMeasureStdsCsv,
  importMeasureStdsCsv
} from '@/api/axios';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import Pagination from '@/components/shared/Pagination.vue';
import { currentRoleFromStoreOrLocalStorage, isAdminRole } from '@/utils/roleAuthorization';
import { Modal } from 'bootstrap';

const PER_PAGE = 50;

export default {
  components: {
    SearchPanelWrapper,
    Pagination
  },
  setup() {
    const title = ref('空気環境測定基準値情報一覧');
    const store = useStore();

    watchEffect(() => {
      document.title = title.value;
    });

    const isAdmin = computed(() => isAdminRole(currentRoleFromStoreOrLocalStorage(store.state.userRole)));
    const router = useRouter();
    const route = useRoute();

    const allMeasureStds = ref([]);
    const measure_stds = ref([]);
    const measureTypes = ref([]);

    const searchMeasureTypeId = ref('');
    const searchMinMaxType = ref('');

    const currentPage = ref(1);
    const totalPages = ref(1);
    const selectedFile = ref(null);
    const errorMessages = ref({});

    const isSearchExpanded = ref(true);
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    const getSearchQuery = () => ({
      measure_std_measure_type_id: searchMeasureTypeId.value,
      measure_std_min_max_type: searchMinMaxType.value,
      measure_std_page: currentPage.value || 1
    });

    const applyPagination = (page = 1) => {
      totalPages.value = Math.max(Math.ceil(allMeasureStds.value.length / PER_PAGE), 1);
      const safePage = Math.min(Math.max(page, 1), totalPages.value);
      currentPage.value = safePage;

      const startIndex = (safePage - 1) * PER_PAGE;
      measure_stds.value = allMeasureStds.value.slice(startIndex, startIndex + PER_PAGE);
    };

    const fetchMeasureTypes = async () => {
      try {
        const response = await getMeasureTypes();
        measureTypes.value = Array.isArray(response.data)
          ? response.data
          : (response.data.measure_types || []);
      } catch (error) {
        console.error('測定種別情報データの取得に失敗しました:', error);
      }
    };

    const fetchMeasureStds = async (page = 1) => {
      try {
        const response = await getMeasureStds({
          m_measure_type_id: searchMeasureTypeId.value,
          min_max_type: searchMinMaxType.value
        });

        allMeasureStds.value = response.data || [];
        applyPagination(page);
      } catch (error) {
        console.error('空気環境測定基準値情報データの取得に失敗しました:', error);
      }
    };

    const handleChangePage = (page) => {
      if (page < 1 || page > totalPages.value) return;

      applyPagination(page);

      const q = getSearchQuery();
      q.measure_std_page = page;
      router.replace({ path: '/measure-stds', query: q });

      if (tableWrapper.value) {
        tableWrapper.value.scrollTop = 0;
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportMeasureStdsCsv({
          m_measure_type_id: searchMeasureTypeId.value,
          min_max_type: searchMinMaxType.value
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `m_measure_stds_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
      }
    };

    const searchMeasureStds = async () => {
      await fetchMeasureStds(1);

      const q = getSearchQuery();
      q.measure_std_page = 1;
      router.push({ path: '/measure-stds', query: q });
    };

    const searchResetMeasureStds = async () => {
      searchMeasureTypeId.value = '';
      searchMinMaxType.value = '';
      await fetchMeasureStds(1);

      const q = getSearchQuery();
      q.measure_std_page = 1;
      router.replace({ path: '/measure-stds', query: q });
    };

    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0];
    };

    const closeModal = () => {
      const modalElement = document.getElementById('measureStdCsvImportModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    const onModalHidden = () => {
      clearErrorMessages();
    };

    const clearErrorMessages = () => {
      errorMessages.value = {};
      selectedFile.value = null;

      const input = document.querySelector('#measureStdCsvImportModal .form-control[type="file"]');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.commit('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importMeasureStdsCsv(selectedFile.value);
        store.commit('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        closeModal();
        document.getElementById('measureStdCsvImportModal').addEventListener('hidden.bs.modal', onModalHidden, { once: true });
        await fetchMeasureStds(currentPage.value || 1);
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.commit('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    onMounted(async () => {
      const q = route.query;
      searchMeasureTypeId.value = q.measure_std_measure_type_id || '';
      searchMinMaxType.value = q.measure_std_min_max_type || '';
      currentPage.value = q.measure_std_page ? Number(q.measure_std_page) : 1;

      await fetchMeasureTypes();
      await fetchMeasureStds(currentPage.value);
    });

    return {
      title,
      isAdmin,
      route,
      measure_stds,
      measureTypes,
      searchMeasureTypeId,
      searchMinMaxType,
      getSearchQuery,
      currentPage,
      totalPages,
      handleChangePage,
      downloadCsv,
      searchMeasureStds,
      searchResetMeasureStds,
      selectedFile,
      errorMessages,
      handleFileChange,
      clearErrorMessages,
      submitFile,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.master-table .btn-cell {
  width: 150px;
  min-width: 150px;
  max-width: 150px;
}
</style>
